import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { X, Plus, Trash2, Save, Lightbulb, ChevronDown, ChevronUp } from 'lucide-react';
import { Button } from '@/components/ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const VolunteerGuidanceBanner = () => {
  const [expanded, setExpanded] = useState(false);
  return (
    <div className="mb-6" data-testid="intake-volunteer-guidance">
      <button
        type="button"
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between px-4 py-3 bg-amber-500/10 border border-amber-500/20 rounded-xl text-amber-300 hover:bg-amber-500/15 transition-colors"
      >
        <span className="flex items-center gap-2 text-sm font-medium">
          <Lightbulb className="w-4 h-4" />
          Volunteer Guidance: Intake Best Practices
        </span>
        {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
      </button>
      {expanded && (
        <div className="mt-2 px-4 py-3 bg-amber-500/5 border border-amber-500/10 rounded-xl space-y-2 text-sm text-slate-400">
          <p className="flex items-start gap-2"><span className="text-amber-400/70 mt-0.5 shrink-0">&bull;</span><span>Approach every conversation with patience, empathy, and respect.</span></p>
          <p className="flex items-start gap-2"><span className="text-amber-400/70 mt-0.5 shrink-0">&bull;</span><span>Never promise assistance during intake. Say: "We're gathering information so our team can understand your situation."</span></p>
          <p className="flex items-start gap-2"><span className="text-amber-400/70 mt-0.5 shrink-0">&bull;</span><span>Focus on families with children, seniors, disabled members, and those who struggle to communicate.</span></p>
          <p className="flex items-start gap-2"><span className="text-amber-400/70 mt-0.5 shrink-0">&bull;</span><span>Assist neighbors in gathering missing documents rather than turning them away.</span></p>
          <p className="flex items-start gap-2"><span className="text-amber-400/70 mt-0.5 shrink-0">&bull;</span><span>Being attentive to sustainability is an act of stewardship, not judgment.</span></p>
        </div>
      )}
    </div>
  );
};

const ExtendedIntakeForm = ({ caseId, neighbor, onClose, onSave }) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    // Applicant Info
    date_of_birth: '',
    preferred_language: 'English',
    marital_status: '',
    household_members: [],
    number_of_household_members: '',
    // Housing Details
    housing_status: '',
    landlord_name: '',
    landlord_contact: '',
    monthly_rent: '',
    amount_past_due: '',
    eviction_notice: null,
    lease_available: null,
    landlord_w9_willing: null,
    // Financial Snapshot
    employment_status: '',
    monthly_income: '',
    monthly_expenses: '',
    number_of_income_sources: '',
    recent_hardship: '',
    other_assistance: '',
    // Documentation Checklist
    has_photo_id: false,
    has_lease: false,
    has_eviction_notice: false,
    has_proof_of_income: false,
    has_w9: false,
    // Stabilization
    stabilization_plan: ''
  });

  const [newMember, setNewMember] = useState({ name: '', age: '', relationship: '' });

  useEffect(() => {
    fetchExistingIntake();
  }, [caseId]);

  const fetchExistingIntake = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/extended-intake/${caseId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data) {
        setForm({
          ...form,
          ...response.data,
          monthly_rent: response.data.monthly_rent || '',
          amount_past_due: response.data.amount_past_due || '',
          monthly_income: response.data.monthly_income || '',
          monthly_expenses: response.data.monthly_expenses || '',
          household_members: response.data.household_members || [],
          marital_status: response.data.marital_status || '',
          number_of_household_members: response.data.number_of_household_members || '',
          number_of_income_sources: response.data.number_of_income_sources || ''
        });
      }
    } catch (error) {
      console.error('Error fetching intake:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const submitData = {
        case_id: caseId,
        ...form,
        monthly_rent: form.monthly_rent ? parseFloat(form.monthly_rent) : null,
        amount_past_due: form.amount_past_due ? parseFloat(form.amount_past_due) : null,
        monthly_income: form.monthly_income ? parseFloat(form.monthly_income) : null,
        monthly_expenses: form.monthly_expenses ? parseFloat(form.monthly_expenses) : null,
        number_of_household_members: form.number_of_household_members ? parseInt(form.number_of_household_members) : null,
        number_of_income_sources: form.number_of_income_sources || null
      };
      
      await axios.post(`${API}/extended-intake`, submitData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Extended intake saved');
      onSave && onSave();
      onClose();
    } catch (error) {
      console.error('Error saving intake:', error);
      toast.error('Failed to save intake');
    } finally {
      setSaving(false);
    }
  };

  const addHouseholdMember = () => {
    if (newMember.name && newMember.relationship) {
      setForm({
        ...form,
        household_members: [...form.household_members, { ...newMember, age: newMember.age ? parseInt(newMember.age) : null }]
      });
      setNewMember({ name: '', age: '', relationship: '' });
    }
  };

  const removeHouseholdMember = (index) => {
    setForm({
      ...form,
      household_members: form.household_members.filter((_, i) => i !== index)
    });
  };

  const inputClass = "w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder:text-slate-500 focus:border-[#0072BC] focus:ring-1 focus:ring-[#0072BC] transition-all outline-none";
  const selectClass = "w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white focus:border-[#0072BC] focus:ring-1 focus:ring-[#0072BC] outline-none appearance-none";
  const labelClass = "block text-sm font-medium text-slate-300 mb-1.5";

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50">
        <div className="bg-slate-900/95 border border-white/10 rounded-2xl p-8">
          <div className="animate-spin w-8 h-8 border-4 border-[#0072BC] border-t-transparent rounded-full mx-auto"></div>
          <p className="mt-4 text-slate-400">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-slate-900/95 border border-white/10 rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col" data-testid="extended-intake-modal">
        {/* Header */}
        <div className="bg-[#0072BC]/10 border-b border-white/10 px-6 py-4 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold text-white">Extended Intake Form</h2>
            <p className="text-sm text-slate-400">{neighbor?.first_name} {neighbor?.last_name}</p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-white hover:bg-white/10 p-2 rounded-lg transition-colors" data-testid="extended-intake-close-btn">
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Form Content */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6">
          {/* Volunteer Guidance Banner */}
          <VolunteerGuidanceBanner />

          <div className="space-y-8">
            {/* Section 1: Applicant Information */}
            <section>
              <h3 className="text-lg font-semibold text-white mb-4 pb-2 border-b border-white/10">
                1. Applicant Information
              </h3>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className={labelClass}>Date of Birth</label>
                  <input
                    type="date"
                    value={form.date_of_birth}
                    onChange={(e) => setForm({...form, date_of_birth: e.target.value})}
                    className={inputClass}
                    data-testid="intake-dob"
                  />
                </div>
                <div>
                  <label className={labelClass}>Preferred Language</label>
                  <select
                    value={form.preferred_language}
                    onChange={(e) => setForm({...form, preferred_language: e.target.value})}
                    className={selectClass}
                    data-testid="intake-language"
                  >
                    <option value="English" className="bg-slate-800">English</option>
                    <option value="Spanish" className="bg-slate-800">Spanish</option>
                    <option value="Polish" className="bg-slate-800">Polish</option>
                    <option value="Other" className="bg-slate-800">Other</option>
                  </select>
                </div>
                <div>
                  <label className={labelClass}>Marital Status</label>
                  <select
                    value={form.marital_status}
                    onChange={(e) => setForm({...form, marital_status: e.target.value})}
                    className={selectClass}
                    data-testid="intake-marital-status"
                  >
                    <option value="" className="bg-slate-800">Select...</option>
                    <option value="Single" className="bg-slate-800">Single</option>
                    <option value="Married" className="bg-slate-800">Married</option>
                    <option value="Divorced" className="bg-slate-800">Divorced</option>
                    <option value="Widowed" className="bg-slate-800">Widowed</option>
                    <option value="Separated" className="bg-slate-800">Separated</option>
                  </select>
                </div>
                <div>
                  <label className={labelClass}>Number of People in Household</label>
                  <input
                    type="number"
                    min="1"
                    value={form.number_of_household_members}
                    onChange={(e) => setForm({...form, number_of_household_members: e.target.value})}
                    className={inputClass}
                    placeholder="Total people living in the household"
                    data-testid="intake-household-count"
                  />
                </div>
              </div>

              {/* Household Members */}
              <div className="mt-4">
                <label className={labelClass}>Household Members</label>
                <div className="bg-white/5 border border-white/10 p-4 rounded-xl">
                  {form.household_members.length > 0 && (
                    <div className="space-y-2 mb-4">
                      {form.household_members.map((member, idx) => (
                        <div key={idx} className="flex items-center gap-2 bg-white/5 p-2 rounded-lg border border-white/10">
                          <span className="flex-1 text-white">{member.name}</span>
                          <span className="text-slate-400">{member.age ? `${member.age} yrs` : ''}</span>
                          <span className="text-slate-400">{member.relationship}</span>
                          <button type="button" onClick={() => removeHouseholdMember(idx)} className="text-rose-400 hover:text-rose-300">
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                  <div className="flex gap-2">
                    <input
                      type="text"
                      placeholder="Name"
                      value={newMember.name}
                      onChange={(e) => setNewMember({...newMember, name: e.target.value})}
                      className={`${inputClass} flex-1`}
                      data-testid="member-name-input"
                    />
                    <input
                      type="number"
                      placeholder="Age"
                      value={newMember.age}
                      onChange={(e) => setNewMember({...newMember, age: e.target.value})}
                      className={`${inputClass} w-20`}
                      data-testid="member-age-input"
                    />
                    <select
                      value={newMember.relationship}
                      onChange={(e) => setNewMember({...newMember, relationship: e.target.value})}
                      className={`${selectClass} w-32`}
                      data-testid="member-relationship-select"
                    >
                      <option value="" className="bg-slate-800">Relation</option>
                      <option value="Spouse" className="bg-slate-800">Spouse</option>
                      <option value="Child" className="bg-slate-800">Child</option>
                      <option value="Parent" className="bg-slate-800">Parent</option>
                      <option value="Sibling" className="bg-slate-800">Sibling</option>
                      <option value="Other" className="bg-slate-800">Other</option>
                    </select>
                    <Button type="button" onClick={addHouseholdMember} size="sm" className="bg-[#0072BC] hover:bg-[#005a96]" data-testid="add-member-btn">
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
            </section>

            {/* Section 2: Housing Details */}
            <section>
              <h3 className="text-lg font-semibold text-white mb-4 pb-2 border-b border-white/10">
                2. Housing Details
              </h3>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className={labelClass}>Current Housing Status</label>
                  <select
                    value={form.housing_status}
                    onChange={(e) => setForm({...form, housing_status: e.target.value})}
                    className={selectClass}
                    data-testid="intake-housing-status"
                  >
                    <option value="" className="bg-slate-800">Select...</option>
                    <option value="Renting" className="bg-slate-800">Renting</option>
                    <option value="Own Home" className="bg-slate-800">Own Home</option>
                    <option value="Staying with Others" className="bg-slate-800">Staying with Others</option>
                    <option value="Homeless" className="bg-slate-800">Homeless</option>
                  </select>
                </div>
                <div>
                  <label className={labelClass}>Landlord Name</label>
                  <input
                    type="text"
                    value={form.landlord_name}
                    onChange={(e) => setForm({...form, landlord_name: e.target.value})}
                    className={inputClass}
                    data-testid="intake-landlord-name"
                  />
                </div>
                <div>
                  <label className={labelClass}>Landlord Phone/Email</label>
                  <input
                    type="text"
                    value={form.landlord_contact}
                    onChange={(e) => setForm({...form, landlord_contact: e.target.value})}
                    className={inputClass}
                    data-testid="intake-landlord-contact"
                  />
                </div>
                <div>
                  <label className={labelClass}>Monthly Rent ($)</label>
                  <input
                    type="number"
                    value={form.monthly_rent}
                    onChange={(e) => setForm({...form, monthly_rent: e.target.value})}
                    className={inputClass}
                    step="0.01"
                    data-testid="intake-monthly-rent"
                  />
                </div>
                <div>
                  <label className={labelClass}>Amount Past Due ($)</label>
                  <input
                    type="number"
                    value={form.amount_past_due}
                    onChange={(e) => setForm({...form, amount_past_due: e.target.value})}
                    className={inputClass}
                    step="0.01"
                    data-testid="intake-amount-past-due"
                  />
                </div>
              </div>
              <div className="grid md:grid-cols-3 gap-4 mt-4">
                <div>
                  <label className={labelClass}>Eviction Notice Received?</label>
                  <select
                    value={form.eviction_notice === null ? '' : form.eviction_notice ? 'yes' : 'no'}
                    onChange={(e) => setForm({...form, eviction_notice: e.target.value === '' ? null : e.target.value === 'yes'})}
                    className={selectClass}
                    data-testid="intake-eviction-notice"
                  >
                    <option value="" className="bg-slate-800">Select...</option>
                    <option value="yes" className="bg-slate-800">Yes</option>
                    <option value="no" className="bg-slate-800">No</option>
                  </select>
                </div>
                <div>
                  <label className={labelClass}>Lease Document Available?</label>
                  <select
                    value={form.lease_available === null ? '' : form.lease_available ? 'yes' : 'no'}
                    onChange={(e) => setForm({...form, lease_available: e.target.value === '' ? null : e.target.value === 'yes'})}
                    className={selectClass}
                    data-testid="intake-lease-available"
                  >
                    <option value="" className="bg-slate-800">Select...</option>
                    <option value="yes" className="bg-slate-800">Yes</option>
                    <option value="no" className="bg-slate-800">No</option>
                  </select>
                </div>
                <div>
                  <label className={labelClass}>Landlord Willing to Complete W-9?</label>
                  <select
                    value={form.landlord_w9_willing === null ? '' : form.landlord_w9_willing ? 'yes' : 'no'}
                    onChange={(e) => setForm({...form, landlord_w9_willing: e.target.value === '' ? null : e.target.value === 'yes'})}
                    className={selectClass}
                    data-testid="intake-landlord-w9"
                  >
                    <option value="" className="bg-slate-800">Select...</option>
                    <option value="yes" className="bg-slate-800">Yes</option>
                    <option value="no" className="bg-slate-800">No</option>
                  </select>
                </div>
              </div>
            </section>

            {/* Section 3: Financial Snapshot */}
            <section>
              <h3 className="text-lg font-semibold text-white mb-4 pb-2 border-b border-white/10">
                3. Financial Snapshot
              </h3>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className={labelClass}>Employment Status</label>
                  <select
                    value={form.employment_status}
                    onChange={(e) => setForm({...form, employment_status: e.target.value})}
                    className={selectClass}
                    data-testid="intake-employment"
                  >
                    <option value="" className="bg-slate-800">Select...</option>
                    <option value="Full-Time" className="bg-slate-800">Full-Time</option>
                    <option value="Part-Time" className="bg-slate-800">Part-Time</option>
                    <option value="Unemployed" className="bg-slate-800">Unemployed</option>
                    <option value="Retired" className="bg-slate-800">Retired</option>
                    <option value="Disabled" className="bg-slate-800">Disabled</option>
                  </select>
                </div>
                <div>
                  <label className={labelClass}>Number of Income Sources</label>
                  <select
                    value={form.number_of_income_sources}
                    onChange={(e) => setForm({...form, number_of_income_sources: e.target.value})}
                    className={selectClass}
                    data-testid="intake-income-sources"
                  >
                    <option value="" className="bg-slate-800">Select...</option>
                    <option value="0" className="bg-slate-800">None</option>
                    <option value="1" className="bg-slate-800">One income</option>
                    <option value="2" className="bg-slate-800">Two incomes</option>
                    <option value="3+" className="bg-slate-800">Three or more</option>
                  </select>
                </div>
                <div>
                  <label className={labelClass}>Monthly Income (All Sources) ($)</label>
                  <input
                    type="number"
                    value={form.monthly_income}
                    onChange={(e) => setForm({...form, monthly_income: e.target.value})}
                    className={inputClass}
                    step="0.01"
                    data-testid="intake-monthly-income"
                  />
                </div>
                <div>
                  <label className={labelClass}>Monthly Expenses ($)</label>
                  <input
                    type="number"
                    value={form.monthly_expenses}
                    onChange={(e) => setForm({...form, monthly_expenses: e.target.value})}
                    className={inputClass}
                    step="0.01"
                    data-testid="intake-monthly-expenses"
                  />
                </div>
              </div>
              <div className="mt-4">
                <label className={labelClass}>Recent Hardship (job loss, illness, etc.)</label>
                <textarea
                  value={form.recent_hardship}
                  onChange={(e) => setForm({...form, recent_hardship: e.target.value})}
                  className={`${inputClass} resize-none`}
                  rows={2}
                  data-testid="intake-hardship"
                />
              </div>
              <div className="mt-4">
                <label className={labelClass}>Other Assistance Received (government aid, nonprofits)</label>
                <textarea
                  value={form.other_assistance}
                  onChange={(e) => setForm({...form, other_assistance: e.target.value})}
                  className={`${inputClass} resize-none`}
                  rows={2}
                  data-testid="intake-other-assistance"
                />
              </div>
            </section>

            {/* Section 4: Documentation Checklist */}
            <section>
              <h3 className="text-lg font-semibold text-white mb-4 pb-2 border-b border-white/10">
                4. Documentation Checklist
              </h3>
              <div className="grid md:grid-cols-2 gap-3">
                {[
                  { key: 'has_photo_id', label: 'Photo ID' },
                  { key: 'has_lease', label: 'Lease Agreement' },
                  { key: 'has_eviction_notice', label: 'Eviction Notice (if applicable)' },
                  { key: 'has_proof_of_income', label: 'Proof of Income' },
                  { key: 'has_w9', label: 'W-9 Form (completed by landlord)' }
                ].map(item => (
                  <label key={item.key} className="flex items-center gap-3 p-3 bg-white/5 border border-white/10 rounded-xl cursor-pointer hover:bg-white/10 transition-colors" data-testid={`doc-${item.key}`}>
                    <input
                      type="checkbox"
                      checked={form[item.key]}
                      onChange={(e) => setForm({...form, [item.key]: e.target.checked})}
                      className="w-5 h-5 rounded border-white/20 bg-white/5 text-[#0072BC] focus:ring-[#0072BC] accent-[#0072BC]"
                    />
                    <span className="text-slate-300">{item.label}</span>
                  </label>
                ))}
              </div>
            </section>

            {/* Section 5: Stabilization Plan */}
            <section>
              <h3 className="text-lg font-semibold text-white mb-4 pb-2 border-b border-white/10">
                5. Stabilization Plan
              </h3>
              <div>
                <label className={labelClass}>How will this assistance help stabilize the situation?</label>
                <textarea
                  value={form.stabilization_plan}
                  onChange={(e) => setForm({...form, stabilization_plan: e.target.value})}
                  className={`${inputClass} resize-none`}
                  rows={3}
                  placeholder="Describe how the requested assistance will help the neighbor achieve stability..."
                  data-testid="intake-stabilization-plan"
                />
              </div>
            </section>
          </div>
        </form>

        {/* Footer */}
        <div className="border-t border-white/10 px-6 py-4 bg-white/5 flex justify-end gap-3">
          <Button type="button" variant="outline" onClick={onClose} className="border-white/20 text-slate-300 hover:bg-white/10" data-testid="intake-cancel-btn">
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={saving} className="bg-[#0072BC] hover:bg-[#005a96]" data-testid="intake-save-btn">
            <Save className="w-4 h-4 mr-2" />
            {saving ? 'Saving...' : 'Save Intake Form'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ExtendedIntakeForm;
